import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { IBasketData } from '@df/session/api/basket/basket';
import { Customer } from '@df/session/customer/customer.interfaces';

import { ExponeaServiceDef } from './exponea.service.def';

declare global {
    interface Window {
        exponea: Exponea.IExponea;
    }
}

declare module '@df/core/app' {
    interface App {
        exponea: ExponeaServiceDef;
    }
}

declare module '@df/core/df-core.config' {
    export interface IDfConfig {
        exponea?: Exponea.IInitConfig;
    }
}

declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        category_root_path?: number[];
    }
}

declare module '@df/catalog/category/category' {
    interface ICategoryDataV2 {
        category_root_path?: number[];
    }
}

declare module '@df/session/api/basket/basket' {
    interface IBasketData {
        coupon_value?: number;
    }
}

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            prefix?: string;
            gender?: string;
            hash?: string;
        }
    }
}

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface ISuccessData {
            status?: string;
            hash?: string;
        }

        interface IAddress {
            title?: string;
        }
    }
}

declare module '@df/session/api/basket/basket' {
    interface IBasketTotals {
        grand_total_global?: number;
    }
}

export namespace Exponea {
    export const KEYS = [
        'initialize',
        'identify',
        'update',
        'track',
        'trackLink',
        'trackEnhancedEcommerce',
        'getHtml',
        'showHtml',
        'showForm',
        'ping',
        'getAbTest'
    ];

    export enum Events {
        ViewCategory = 'view_category', //
        ViewProduct = 'view_item', //
        CartUpdate = 'cart_update', //
        Search = 'onsite_search',
        Checkout = 'checkout', //
        Wishlist = 'wishlist',
        NewsletterConsent = 'consent', //
        PurchaseItem = 'purchase_item', //
        Purchase = 'purchase', //
        Authentication = 'authentication', //
        Filter = 'filter',
        StockNotification = 'lead_watchdog_stock' //
    }

    export enum CartUpdateActions {
        Add = 'add',
        Remove = 'remove',
        Quantity = 'quantity_update',
        PromoAdd = 'add_promo',
        PromoRemove = 'remove_promo'
    }

    export enum WishlistActions {
        Add = 'add',
        Remove = 'remove'
    }

    export enum NewsletterConsentActions {
        Accept = 'accept',
        Reject = 'reject'
    }

    export type ItemsCartUpdateAction = CartUpdateActions.Add | CartUpdateActions.Remove | CartUpdateActions.Quantity;

    export type PromoCartUpdateAction = CartUpdateActions.PromoAdd | CartUpdateActions.PromoRemove;

    export interface IExponea {
        _: any[];
        __: any;
        initialize(config: IInitConfig): void;
        update(customerData: ICustomerUpdate): void;
        identify(customerIds: ICustomerIdentify | string, customerProperties?: Record<string, unknown>): void;
        track(eventId: Events, eventProperties: IEventProperties): void;
        getRecommendation(options: IRecommendationsOptions): void;
    }

    export interface IInitConfig {
        token: string;
        track?: ITrackConfig;
        cookies?: ICookiesConfig;
        debug?: boolean;
    }

    export interface ICookiesConfig {
        cross_subdomain?: boolean;
        retrieve_callback?: () => void;
    }

    export interface ITrackConfig {
        auto?: boolean;
        visits?: boolean;
        activity?: boolean;
        unloads?: boolean;
        exits?: boolean;
        default_properties?: { [key: string]: any };
        google_analytics?: boolean;
    }

    export interface IDataLayerEvent extends IEvent {
        namespace: 'exponea';
    }

    export interface IEvent {
        name: Events;
        properties: IEventProperties;
    }

    export interface IEventProperties {
        email?: string;
    }

    export interface IViewCategoryEventProperties extends IEventProperties {
        category_id: string;
        category_name: string;
        category_listed_products?: string[];
        category_1: string;
        categories_path: string;
        categories_ids?: string[];
        local_currency: string;
    }

    export interface IViewProductEventProperties extends IItemEventProperties {
        stock_level: number;
    }

    export interface ISearchEventProperties extends IEventProperties {
        search_term: string;
        number_of_search_results: number;
    }

    export interface IProductEventProperties extends IEventProperties {
        product_id: string;
        title: string;
        category_1?: string;
        category_2?: string;
        category_3?: string;
        categories_path: string;
        category_id: string;
        categories_ids?: string[];
    }

    export interface IItemEventProperties extends IProductEventProperties {
        price: number;
        price_local_currency: number;
        discount_value: number;
        original_price?: number;
        original_price_local_currency?: number;
        local_currency: string;
    }

    export interface ICartUpdateEventProperties extends ICartEventProperties {
        action: CartUpdateActions;
        page_type?: string;
    }

    export interface IBasketItemCartUpdateEventProperties extends IPromoCartUpdateEventProperties, IItemEventProperties {}

    export interface ICurrentPromo {
        code: string | undefined | null;
        value: number;
    }

    export interface IPromoCartUpdateEventProperties extends ICartUpdateEventProperties {
        promo_code: string;
        promo_value: number;
    }

    export interface ICartEventProperties extends IEventProperties {
        product_list: (ICartProductListItem | undefined)[];
        product_ids: (string | undefined)[];
        total_quantity: number;
        total_price: number;
        total_price_local_currency: number;
    }

    export interface ICartProductListItem {
        product_id: string;
        quantity: number;
    }

    export interface ICheckoutStepChangeEventProperties extends ICheckoutEventProperties {
        step_title: string;
        step_number?: number;
        promo_total_value: number;
    }

    export interface ICheckoutEventProperties extends ICartEventProperties {
        promo_code?: string;
        payment_type?: string;
        delivery_type?: string;
        delivery_cost?: number;
        delivery_country?: string;
        delivery_city?: string;
        tax_value?: number;
        local_currency?: string;
        total_price_without_tax: number;
    }

    export interface IWishlistChangeEventProperties extends IEventProperties {
        action: WishlistActions;
        price: number;
        price_local_currency: number;
        discount_value: number;
        original_price: number;
        original_price_local_currency: number;
        local_currency: string;
        product_id: string;
        title: string;
        category_1: string;
    }

    export interface INewsletterConsent extends IEventProperties {
        action: NewsletterConsentActions;
        category: 'weekly_newsletters_from_web' | 'newsletter';
        timestamp: number;
        identification_type: 'email';
        identification: string;
        source: 'public_api' | string;
        placement?: 'public_api' | string;
        valid_until: 'unlimited';
        message: string;
        current_website: string;
        optin_date?: string;
    }

    export interface ICustomerIdentify {
        registered: string;
    }

    export interface ICustomerUpdate {
        first_name?: string;
        last_name?: string;
        gender?: string;
        title?: string;
        city?: string;
        country?: string;
        phone?: string;
        email?: string;
        birth_date?: number;
        cookies_accepted?: 'yes' | 'no';
        watchdog_email?: string;
    }

    export interface ICustomerAuthenticationEventProperties extends IEventProperties {
        logged_in: boolean;
        user_id?: string;
    }

    export interface ICheckoutSubmitBeforeEventData {
        checkout: CheckoutFormComponentDef;
        data: Checkout.ISubmissionData;
    }

    export interface ICheckoutSubmitData {
        submissionData: Checkout.ISubmissionData;
        basketData: IBasketData;
        customerData: Customer.ICustomerData;
        checkoutBackendData: Checkout.IData;
        checkoutData: Checkout.IQuoteData;
    }

    export interface IPurchaseProperties extends IEventProperties {
        purchase_id?: string;
        purchase_status?: string;
        total_price: number;
        total_price_without_tax: number;
        total_price_local_currency: number;
    }

    export interface IPurchaseItemEventProperties extends IItemEventProperties, IPurchaseProperties {
        quantity: number;
        discount_percentage: number;
    }

    export interface IPurchaseEventProperties extends IPurchaseProperties, ICheckoutEventProperties {
        promo_value: number;
    }

    export interface IFilterEventProperties extends IEventProperties {
        filter_name: string;
        filter_category: string;
    }

    export interface IStockNotificationEventProperties extends IEventProperties {
        variant_id: string;
        store_id?: string;
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Recommendations
    ////////
    ////////////////////////////////////////////////

    export interface IRecommendationsOptions {
        recommendationId: string;
        fillWithRandom?: boolean;
        callback: (items: IRecommendationsItem[] | null) => void;
        // 10 default
        size?: number;
        // If present, the recommendations are related not only to a customer, but to
        // products with IDs specified in this array. Item IDs from catalog used to train the
        // recommendation model need to be used. Input product IDs in a dictionary as
        // {product_id: weight}, where the value weight determines the preference strength
        // for the given product (bigger number = higher preference).
        // Example:
        // {"product_id_1": 1, "product_id_2": 2,}
        items?: IRecommendationsOptionsItems;
        catalogFilter?: IRecommendationsCatalogFilter[];
        catalogAttributesWhitelist?: string[];
        strategy?: RecommendationsStrategy;
        categoryNames?: string[];
    }

    export interface IRecommendationsOptionsItems {
        [productId: string]: number;
    }

    export interface IRecommendationsItem {
        active?: boolean;
        category_1_url?: string;
        category_id?: string;
        category_level_1?: string;
        category_level_2?: string;
        category_level_3?: string;
        category_path?: string;
        color?: string;
        date_added: number;
        description?: string;
        discount_value?: number;
        engine_name?: string;
        image?: string;
        image_fullsize?: string;
        item_id?: string;
        original_price?: number;
        price?: number;
        product_id?: string;
        recommendation_id: string;
        recommendation_variant_id?: string;
        stock_level?: number;
        title?: string;
        url?: string;
    }

    export interface IRecommendationsCatalogFilter {
        constraint: IRecommendationsCatalogFilterConstraint;
        property: string;
    }

    export interface IRecommendationsCatalogFilterConstraint {
        operands: IRecommendationsCatalogFilterConstraintOperand[];
        operator: RecommendationsCatalogFilterOperator;
        type: string;
    }

    export interface IRecommendationsCatalogFilterConstraintOperand {
        type: string;
        value: string;
    }

    export type RecommendationsCatalogFilterOperator =
        | RecommendationsCatalogFilterCommonOperators
        | RecommendationsCatalogFilterStringsOperators
        | RecommendationsCatalogFilterNumbersOperators
        | RecommendationsCatalogFilterBooleanOperators
        | RecommendationsCatalogFilterDatesOperators;

    export enum RecommendationsCatalogFilterCommonOperators {
        IsSet = 'is set',
        IsNotSet = 'is not set',
        HasValue = 'has value',
        HasNoValue = 'has no value'
    }

    export enum RecommendationsCatalogFilterStringsOperators {
        Equals = 'equals',
        DoesNotEqual = 'does not equal',
        In = 'in',
        NotIn = 'not in',
        Contains = 'contains',
        DoesNotContain = 'does not contain',
        StartsWith = 'starts with',
        EndsWith = 'ends with',
        MatchesRegex = 'matches regex'
    }

    export enum RecommendationsCatalogFilterNumbersOperators {
        EqualTo = 'equal to',
        InBetween = 'in between',
        LessThan = 'less than',
        GreaterThan = 'greater than'
    }

    export enum RecommendationsCatalogFilterBooleanOperators {
        IsTrue = 'is true',
        IsFalse = 'is false'
    }

    export enum RecommendationsCatalogFilterDatesOperators {
        MoreThan = 'more than',
        LessThan = 'less than',
        MatchesRange = 'matches range',
        MatchesCurrentDay = 'matches current day',
        MatchesCurrentMonth = 'matches current month',
        MatchesCurrentYear = 'matches current year',
        SecondMatches = 'second matches',
        MinuteMatches = 'minute matches',
        HourMatches = 'hour matches',
        WeekdayMatches = 'weekday matches',
        DayMatches = 'day matches',
        MonthMatches = 'month matches',
        YearMatches = 'year matches'
    }

    export enum RecommendationsStrategy {
        Winner = 'winner',
        Mix = 'mix'
    }

    export interface IRecommendationsEngineOptions {
        options?: string | IRecommendationsOptions;
    }

    export const recommendationsEngineId = 'exponeaRecommendations';

    export const recommendationsOptions = 'exponeaOptions';

    export interface IStockNotificationData {
        newsletter?: boolean;
        email: string;
    }
}
